import React, { useState, useEffect, useCallback } from 'react';
import { Exemplar, Book, CreateExemplarData, ValidationError } from '../types';
import { exemplarsApi, booksApi, getImageUrl, getThumbnailUrl } from '../services/api';

/**
 * Componente ExemplarManager
 *
 * Cadastro e manutenção dos exemplares em estoque (Mauá):
 * - Formulário: OP, título, quantidade, projeto vinculado (opcional),
 *   localização e observações, 1 foto
 * - Lista com edição de quantidade rápida, editar e remover
 */

interface ExemplarFormState {
  opNumber: string;
  title: string;
  quantity: string;
  bookId: string;
  location: string;
  locationDetail: string;
  notes: string;
}

const EMPTY_FORM: ExemplarFormState = {
  opNumber: '',
  title: '',
  quantity: '1',
  bookId: '',
  location: 'Mauá',
  locationDetail: '',
  notes: ''
};

export function ExemplarManager(): React.ReactElement {
  const [exemplars, setExemplars] = useState<Exemplar[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Formulário
  const [form, setForm] = useState<ExemplarFormState>(EMPTY_FORM);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // Edição
  const [editingId, setEditingId] = useState<number | null>(null);

  // Rascunho da edição inline de quantidade — o PUT só acontece no blur/Enter,
  // nunca a cada tecla (evita gravar valores transitórios, ex.: backspace em
  // "10" salvando 1)
  const [qtyDrafts, setQtyDrafts] = useState<Record<number, string>>({});

  const loadExemplars = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await exemplarsApi.getAll();
      setExemplars(data);
    } catch (err: any) {
      setMessage(err?.message || 'Erro ao carregar exemplares');
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExemplars();
    // Projetos para o select de vínculo opcional
    booksApi.getAll().then(setBooks).catch(console.error);
  }, [loadExemplars]);

  const showFeedback = (text: string) => {
    setMessage(text);
    setTimeout(() => setMessage(null), 3000);
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setPhoto(null);
    setPhotoPreview(null);
    setEditingId(null);
    setErrors([]);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setPhoto(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setPhotoPreview(null);
    }
  };

  const startEdit = (exemplar: Exemplar) => {
    setEditingId(exemplar.id);
    setForm({
      opNumber: exemplar.opNumber,
      title: exemplar.title,
      quantity: exemplar.quantity.toString(),
      bookId: exemplar.bookId ? exemplar.bookId.toString() : '',
      location: exemplar.location,
      locationDetail: exemplar.locationDetail || '',
      notes: exemplar.notes || ''
    });
    // Na edição, a foto existente só é substituída se um novo arquivo for enviado
    setPhoto(null);
    setPhotoPreview(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: ValidationError[] = [];
    if (!form.opNumber.trim()) errs.push({ field: 'opNumber', message: 'Número da OP é obrigatório' });
    if (!form.title.trim()) errs.push({ field: 'title', message: 'Título é obrigatório' });
    const quantity = parseInt(form.quantity, 10);
    if (isNaN(quantity) || quantity < 0) errs.push({ field: 'quantity', message: 'Quantidade deve ser um inteiro ≥ 0' });
    setErrors(errs);
    if (errs.length > 0) return;

    setIsSubmitting(true);
    try {
      const parsedBookId = form.bookId ? parseInt(form.bookId, 10) : null;

      if (editingId !== null) {
        await exemplarsApi.update(editingId, {
          opNumber: form.opNumber.trim(),
          title: form.title.trim(),
          quantity,
          bookId: parsedBookId,
          location: form.location.trim() || 'Mauá',
          locationDetail: form.locationDetail.trim(),
          notes: form.notes.trim()
        }, photo || undefined);
        showFeedback('Exemplar atualizado!');
      } else {
        const data: CreateExemplarData = {
          opNumber: form.opNumber.trim(),
          title: form.title.trim(),
          quantity,
          bookId: parsedBookId,
          location: form.location.trim() || 'Mauá',
          locationDetail: form.locationDetail.trim(),
          notes: form.notes.trim()
        };
        await exemplarsApi.create(data, photo || undefined);
        showFeedback('Exemplar cadastrado!');
      }
      resetForm();
      loadExemplars();
    } catch (err: any) {
      showFeedback(err?.message || 'Erro ao salvar exemplar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearQtyDraft = (id: number) => {
    setQtyDrafts(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const commitQuantity = async (exemplar: Exemplar) => {
    const draft = qtyDrafts[exemplar.id];
    if (draft === undefined) return; // nada em edição

    const parsed = parseInt(draft, 10);
    if (isNaN(parsed) || parsed < 0 || parsed === exemplar.quantity) {
      clearQtyDraft(exemplar.id); // inválido ou inalterado: volta ao valor do servidor
      return;
    }

    try {
      await exemplarsApi.update(exemplar.id, { quantity: parsed });
      setExemplars(prev => prev.map(ex => ex.id === exemplar.id ? { ...ex, quantity: parsed } : ex));
      clearQtyDraft(exemplar.id);
    } catch (err: any) {
      showFeedback(err?.message || 'Erro ao atualizar quantidade');
      // mantém o rascunho para o usuário corrigir
    }
  };

  const handleDelete = async (exemplar: Exemplar) => {
    if (!confirm(`Remover o exemplar "${exemplar.title}" (OP ${exemplar.opNumber})?`)) return;
    try {
      await exemplarsApi.delete(exemplar.id);
      showFeedback('Exemplar removido.');
      if (editingId === exemplar.id) resetForm();
      loadExemplars();
    } catch (err: any) {
      showFeedback(err?.message || 'Erro ao remover exemplar');
    }
  };

  const inputStyle = { width: '100%', padding: '12px', border: '1px solid #d6d9e6', borderRadius: '8px', boxSizing: 'border-box' as const, fontSize: '0.9375rem' };
  const labelStyle = { display: 'block', marginBottom: '8px', fontWeight: 600 as const, color: '#373a50', fontSize: '0.9375rem' };

  const fieldError = (field: string) =>
    errors.find(e => e.field === field)?.message;

  return (
    <div>
      {/* ===== Formulário ===== */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: 'var(--shadow-md)', marginBottom: '25px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#191364' }}>
            {editingId !== null ? '✏️ Editar Exemplar' : '📦 Cadastrar Exemplar em Estoque'}
          </h2>
          {editingId !== null && (
            <button type="button" onClick={resetForm} style={{ padding: '8px 16px', background: '#ffffff', color: '#373a50', border: '1px solid #d6d9e6', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              + Novo em vez de editar
            </button>
          )}
        </div>

        {message && (
          <div style={{ padding: '12px', background: message.includes('Erro') ? '#fee2e2' : '#f0faf4', marginBottom: '20px', borderRadius: '8px', color: message.includes('Erro') ? '#b91c1c' : '#206f40' }}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>Número da OP *</label>
              <input
                type="text"
                value={form.opNumber}
                onChange={e => setForm(prev => ({ ...prev, opNumber: e.target.value }))}
                style={inputStyle}
                disabled={isSubmitting}
                placeholder="Ex: 12345"
              />
              {fieldError('opNumber') && <span style={{ color: '#dc2626', fontSize: '13px' }}>{fieldError('opNumber')}</span>}
            </div>

            <div>
              <label style={labelStyle}>Quantidade *</label>
              <input
                type="number"
                min={0}
                step={1}
                value={form.quantity}
                onChange={e => setForm(prev => ({ ...prev, quantity: e.target.value }))}
                style={inputStyle}
                disabled={isSubmitting}
              />
              {fieldError('quantity') && <span style={{ color: '#dc2626', fontSize: '13px' }}>{fieldError('quantity')}</span>}
            </div>

            <div>
              <label style={labelStyle}>Localização</label>
              <input
                type="text"
                value={form.location}
                onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
                style={inputStyle}
                disabled={isSubmitting}
                placeholder="Ex: Mauá"
              />
            </div>

            <div>
              <label style={labelStyle}>Detalhe da localização</label>
              <input
                type="text"
                value={form.locationDetail}
                onChange={e => setForm(prev => ({ ...prev, locationDetail: e.target.value }))}
                style={inputStyle}
                disabled={isSubmitting}
                placeholder="Ex: Estante B, prateleira 3"
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Título *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
              style={inputStyle}
              disabled={isSubmitting}
              placeholder="Título do exemplar (como está na OP)"
            />
            {fieldError('title') && <span style={{ color: '#dc2626', fontSize: '13px' }}>{fieldError('title')}</span>}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Projeto vinculado (opcional)</label>
            <select
              value={form.bookId}
              onChange={e => setForm(prev => ({ ...prev, bookId: e.target.value }))}
              style={inputStyle}
              disabled={isSubmitting}
            >
              <option value="">Sem vínculo com projeto do catálogo</option>
              {books.map(book => (
                <option key={book.id} value={book.id}>{book.title}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Observações</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
              disabled={isSubmitting}
              placeholder="Ex: separado para a feira XYZ"
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Foto da caixa (1 imagem)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <label style={{ padding: '12px 24px', background: '#ffffff', color: '#373a50', border: '1px solid #d6d9e6', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'inline-block', fontSize: '0.9375rem' }}>
                📷 Escolher Foto
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoSelect}
                  disabled={isSubmitting}
                  style={{ display: 'none' }}
                />
              </label>
              <span style={{ color: '#373a50', fontSize: '0.9375rem' }}>
                {photo ? photo.name : editingId !== null ? 'Sem alteração (mantém a foto atual)' : 'Nenhum arquivo selecionado'}
              </span>
              {photoPreview && (
                <div style={{ position: 'relative' }}>
                  <img src={photoPreview} alt="Prévia" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #d6d9e6' }} />
                  <button
                    type="button"
                    onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                    style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '13px' }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '20px', borderTop: '1px solid #e5e8f1' }}>
            <button type="button" onClick={resetForm} disabled={isSubmitting} style={{ padding: '12px 24px', background: '#ffffff', color: '#373a50', border: '1px solid #d6d9e6', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9375rem' }}>
              Limpar
            </button>
            <button type="submit" disabled={isSubmitting} style={{ padding: '12px 24px', background: '#2f8a55', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9375rem' }}>
              {isSubmitting ? 'Salvando...' : editingId !== null ? 'Salvar Alterações' : 'Cadastrar Exemplar'}
            </button>
          </div>
        </form>
      </div>

      {/* ===== Lista ===== */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: 'var(--shadow-md)' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#191364' }}>📋 Exemplares em Estoque ({exemplars.length})</h2>

        {isLoading ? (
          <p>Carregando...</p>
        ) : exemplars.length === 0 ? (
          <p style={{ color: '#5a5d7a' }}>Nenhum exemplar cadastrado ainda.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {exemplars.map(exemplar => (
              <div key={exemplar.id} style={{ border: editingId === exemplar.id ? '1px solid #cbe9d8' : '1px solid #e5e8f1', borderRadius: '10px', padding: '14px', display: 'flex', gap: '12px', background: editingId === exemplar.id ? '#f0faf4' : '#fafbfd' }}>
                {/* Foto */}
                <div style={{ width: '72px', height: '96px', flexShrink: 0, borderRadius: '8px', overflow: 'hidden', background: '#e5e8f1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {exemplar.photoFilepath ? (
                    <a href={getImageUrl(exemplar.photoFilepath)} target="_blank" rel="noopener noreferrer" title="Ver foto em tamanho original">
                      <img src={getThumbnailUrl(exemplar.photoFilepath)} alt={exemplar.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                    </a>
                  ) : (
                    <span style={{ fontSize: '1.6rem', opacity: 0.4 }}>📦</span>
                  )}
                </div>

                {/* Dados */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ color: '#206f40', fontWeight: 700, fontSize: '0.8125rem', textTransform: 'uppercase' }}>OP {exemplar.opNumber}</span>
                  <strong style={{ color: '#191364', fontSize: '0.9375rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={exemplar.title}>{exemplar.title}</strong>
                  {exemplar.book && (
                    <span style={{ color: '#206f40', fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={exemplar.book.title}>
                      📁 {exemplar.book.title}
                    </span>
                  )}
                  <span style={{ color: '#5a5d7a', fontSize: '0.8125rem' }}>
                    📍 {exemplar.location}{exemplar.locationDetail ? ` — ${exemplar.locationDetail}` : ''}
                  </span>

                  {/* Quantidade editável inline (salva ao sair do campo ou dar Enter) */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.875rem', color: '#19361f', marginTop: '2px' }}>
                    Qtde:
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={qtyDrafts[exemplar.id] ?? exemplar.quantity}
                      onChange={e => setQtyDrafts(prev => ({ ...prev, [exemplar.id]: e.target.value }))}
                      onBlur={() => commitQuantity(exemplar)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.currentTarget.blur(); // dispara o commit do onBlur
                        }
                      }}
                      style={{ width: '64px', padding: '4px 6px', border: '1px solid #d6d9e6', borderRadius: '8px', fontSize: '0.875rem' }}
                      title="Quantidade disponível (Enter ou clicar fora para salvar)"
                    />
                  </label>

                  {/* Ações */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '6px' }}>
                    <button onClick={() => startEdit(exemplar)} style={{ flex: 1, padding: '6px', background: '#fef3c7', color: '#b45309', border: 'none', borderRadius: '8px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 600 }}>
                      ✏️ Editar
                    </button>
                    <button onClick={() => handleDelete(exemplar)} style={{ flex: 1, padding: '6px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '8px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 600 }}>
                      🗑️ Remover
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExemplarManager;
